import { Box, Typography, keyframes } from '@mui/material';
import PropTypes from 'prop-types';
import {
  colorPrimary,
  colorTextBlack,
  colorTextGrey
} from '../../config/theme';
import AdminHubCard from './AdminHubCard';

const floatOrb = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(12px, -10px); }
`;

const heroFade = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

function AdminHubLayout({ title, subtitle, sections, onNavigate }) {
  let cardIndex = 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: 'calc(100vh - 120px)',
        borderRadius: { md: '12px' },
        overflow: 'hidden',
        background:
          'linear-gradient(145deg, #e8eef5 0%, #f5f7fa 40%, #fef9ec 75%, #f0f4f8 100%)',
        p: { xs: 0, md: 0.5 }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -60,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colorPrimary}40 0%, transparent 68%)`,
          animation: `${floatOrb} 9s ease-in-out infinite`,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -40,
          left: -60,
          width: 260,
          height: 260,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)',
          animation: `${floatOrb} 11s ease-in-out infinite reverse`,
          pointerEvents: 'none'
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3 },
            borderRadius: '20px',
            background: 'rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(18px) saturate(180%)',
            WebkitBackdropFilter: 'blur(18px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.65)',
            boxShadow: '0 8px 28px rgba(31, 41, 51, 0.06)',
            animation: `${heroFade} 0.5s ease-out both`
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '1.75rem', md: '2.25rem' },
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: colorTextBlack,
              mb: 0.75
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontSize: { xs: '0.9rem', md: '1rem' },
                fontWeight: 500,
                color: colorTextGrey,
                maxWidth: 560,
                lineHeight: 1.5
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {sections.map((section, sectionIdx) => (
          <Box
            key={section.title}
            sx={{ mb: sectionIdx < sections.length - 1 ? 3.5 : 0 }}
          >
            <Box
              sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
            >
              <Box
                sx={{
                  width: 4,
                  height: 22,
                  borderRadius: 4,
                  bgcolor: colorPrimary
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 800,
                    color: colorTextBlack,
                    letterSpacing: '-0.01em'
                  }}
                >
                  {section.title}
                </Typography>
                {section.subtitle && (
                  <Typography
                    sx={{ fontSize: '0.8rem', color: colorTextGrey, mt: 0.25 }}
                  >
                    {section.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: `repeat(${Math.min(section.columns || 3, 2)}, minmax(0, 1fr))`,
                  lg: `repeat(${section.columns || 3}, minmax(0, 1fr))`
                },
                gap: { xs: 2, md: 2.5 }
              }}
            >
              {section.items.map(item => {
                const idx = cardIndex++;
                return (
                  <AdminHubCard
                    key={item.path}
                    title={item.title}
                    description={item.description}
                    accent={item.accent}
                    Icon={item.Icon}
                    index={idx}
                    onClick={() => onNavigate(item.menu_name, item.path, item)}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

AdminHubLayout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      columns: PropTypes.number,
      items: PropTypes.array.isRequired
    })
  ).isRequired,
  onNavigate: PropTypes.func.isRequired
};

export default AdminHubLayout;
